import { useState } from "react";
import { useLocation } from "react-router-dom";
import { RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAppVersion } from "@/hooks/useAppVersion";

// Non-intrusive banner shown when /version.json reports a newer build than
// the one this tab loaded. Sits at the bottom-right so it never blocks the
// active form / DealIQ checker. Admin routes get stronger copy.
export function UpdateBanner() {
  const { updateAvailable } = useAppVersion();
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(false);

  if (!updateAvailable || dismissed) return null;

  const isAdmin = pathname.startsWith("/admin");
  const title = isAdmin ? "Admin update available" : "New version available";
  const body = isAdmin
    ? "Refresh to load the latest tools and avoid stale admin data."
    : "Refresh to get the latest improvements.";

  const handleRefresh = () => {
    // Hard reload to bypass any in-memory module cache.
    window.location.reload();
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 right-4 z-[100] max-w-sm rounded-lg border border-border bg-card p-4 shadow-lg animate-in slide-in-from-bottom-4"
    >
      <div className="flex items-start gap-3">
        <RefreshCw className="h-5 w-5 text-primary shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-foreground">{title}</p>
          <p className="text-sm text-muted-foreground mt-0.5">{body}</p>
          <div className="mt-3 flex gap-2">
            <Button size="sm" onClick={handleRefresh}>
              Refresh now
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setDismissed(true)}>
              Later
            </Button>
          </div>
        </div>
        <button
          aria-label="Dismiss"
          onClick={() => setDismissed(true)}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
